import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listSummaries, getSummary, getEmploymentInfo, getWarnings } from '../lib/db.js';
import { StatusBar } from '../components/StatusBar.jsx';
import { BottomNav } from '../components/BottomNav.jsx';
import { severanceCalc, baht } from '../lib/severance.js';

const CHECKLIST = [
  'หนังสือเลิกจ้างเป็นลายลักษณ์อักษร ระบุเหตุผลชัดเจน',
  'ใบเตือนครบขั้นตาม ladder (กรณีเลิกจ้างเพราะวินัย)',
  'จ่ายค่าชดเชย + ค่าบอกกล่าวล่วงหน้าครบถ้วน',
  'จ่ายค่าจ้าง/วันลาพักร้อนคงเหลือ + คืน-หักทรัพย์สิน',
  'ออกใบผ่านงาน + แจ้งออกประกันสังคม',
];

export function Severance() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [emps, setEmps] = useState([]);
  const [empId, setEmpId] = useState('');
  const [tenureDays, setTenureDays] = useState(0);
  const [salary, setSalary] = useState('');
  const [maxWarnLevel, setMaxWarnLevel] = useState(0);
  const [err, setErr] = useState('');

  useEffect(() => {
    listSummaries().then((rows) => {
      setEmps(rows);
      const pre = params.get('emp');
      setEmpId(pre && rows.some((r) => r.id === pre) ? pre : rows[0]?.id || '');
    }).catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    if (!empId) return;
    let active = true;
    (async () => {
      try {
        const [s, emp, w] = await Promise.all([getSummary(empId), getEmploymentInfo(empId), getWarnings(empId)]);
        if (!active) return;
        setTenureDays(s?.tenure_days ?? 0);
        setSalary(emp?.base_salary != null ? String(emp.base_salary) : '');
        setMaxWarnLevel((w || []).reduce((m, x) => Math.max(m, x.ladder_level || 0), 0));
      } catch (e) { if (active) setErr(e.message); }
    })();
    return () => { active = false; };
  }, [empId]);

  const calc = useMemo(() => severanceCalc(tenureDays, salary), [tenureDays, salary]);
  const ladderOk = maxWarnLevel >= 3;

  return (
    <div className="phone">
      <StatusBar />
      <section className="screen">
        <button className="back" onClick={() => nav(-1)}>‹ กลับ</button>
        <div className="title-row">
          <div><div className="eyebrow">ตัวช่วยคำนวณ — กันเลิกจ้างไม่เป็นธรรม</div><h1 className="page">ค่าชดเชย</h1></div>
          <span className="glyph">🧮</span>
        </div>

        {err && <p className="err">{err}</p>}

        <label className="f">พนักงาน</label>
        <select value={empId} onChange={(e) => setEmpId(e.target.value)}>
          {emps.map((e) => <option key={e.id} value={e.id}>{(e.nickname || e.first_name)} · {e.position}</option>)}
        </select>

        <label className="f">อายุงาน (วัน)</label>
        <input type="number" value={tenureDays} onChange={(e) => setTenureDays(Number(e.target.value) || 0)} />
        <label className="f">เงินเดือนฐาน (บาท/เดือน)</label>
        <input type="number" value={salary} onChange={(e) => setSalary(e.target.value)} placeholder="เช่น 18000" />

        <div className="hero" style={{ margin: '22px 0 8px' }}>
          <div className="num small">{baht(calc.total).replace('฿', '')}</div>
          <div className="sub">รวมที่ต้องจ่ายโดยประมาณ (บาท) · {calc.tenureLabel}</div>
        </div>

        {!calc.eligible && (
          <div className="note" style={{ color: 'var(--warn)', marginTop: 4 }}>
            อายุงานน้อยกว่า 120 วัน — ยังไม่มีสิทธิค่าชดเชยตามกฎหมาย (ยังต้องจ่ายค่าบอกกล่าวล่วงหน้า)
          </div>
        )}

        <div className="sec-label">รายละเอียด</div>
        <div className="def"><span className="dk">ขั้นค่าชดเชย</span><span className="dv">{calc.tier ? calc.tier.label : '—'}</span></div>
        <div className="def"><span className="dk">ค่าชดเชย ({calc.severanceDays} วัน)</span><span className="dv">{baht(calc.severanceAmount)}</span></div>
        <div className="def"><span className="dk">ค่าบอกกล่าวล่วงหน้า (1 งวด)</span><span className="dv">{baht(calc.noticeAmount)}</span></div>
        <div className="def"><span className="dk" style={{ fontWeight: 700, color: 'var(--t1)' }}>รวมโดยประมาณ</span><span className="dv" style={{ fontSize: 16 }}>{baht(calc.total)}</span></div>

        <div className="sec-label">ตรวจ ladder ก่อนเลิกจ้าง (กรณีวินัย)</div>
        <div className="leave-note" style={ladderOk ? undefined : { background: 'color-mix(in srgb,var(--warn) 12%,transparent)', borderWidth: 1, borderStyle: 'solid', borderColor: 'color-mix(in srgb,var(--warn) 35%,transparent)' }}>
          {ladderOk
            ? <span><b>✓ ใบเตือนถึงขั้นสูง</b> (ขั้น {maxWarnLevel}) — ดำเนินขั้นตอนวินัยมาแล้ว</span>
            : <span style={{ color: 'var(--warn)' }}><b>⚠︎ ใบเตือนยังไม่ครบขั้น</b> (สูงสุดขั้น {maxWarnLevel}) — เลิกจ้างเพราะวินัยตอนนี้เสี่ยง "ไม่เป็นธรรม"</span>}
        </div>

        <div className="sec-label">เช็กลิสต์ก่อนเลิกจ้าง</div>
        {CHECKLIST.map((c, i) => (
          <div className="row" key={i}>
            <div className="ic">☐</div>
            <div className="main"><div className="l1" style={{ whiteSpace: 'normal', fontWeight: 500, fontSize: 14 }}>{c}</div></div>
          </div>
        ))}

        <div className="leave-note" style={{ background: 'color-mix(in srgb,var(--warn) 12%,transparent)', borderWidth: 1, borderStyle: 'solid', borderColor: 'color-mix(in srgb,var(--warn) 35%,transparent)', marginTop: 18 }}>
          <b style={{ color: 'var(--warn)' }}>⚠︎ ควรปรึกษาทนายแรงงานก่อนดำเนินการเสมอ</b> — ตัวเลขนี้เป็นการประมาณเบื้องต้น
          ไม่รวมกรณีเลิกจ้างได้โดยไม่ต้องจ่ายค่าชดเชย (ม.119) และเงื่อนไขเฉพาะอื่น ๆ
        </div>
      </section>
      <BottomNav />
    </div>
  );
}
